import { Component, Inject, OnDestroy, OnInit } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { ToastrService } from 'ngx-toastr';
import { CollisService } from '../../../core/services/collis/collis.service';
import { Subject, Subscription, of } from 'rxjs';
import { catchError, debounceTime, distinctUntilChanged, switchMap } from 'rxjs/operators';

export interface CustomerOption {
  name: string;
  phone: string;
}

@Component({
  selector: 'app-changereceivermodal',
  templateUrl: './changereceivermodal.component.html',
  styleUrls: ['./changereceivermodal.component.css']
})
export class ChangereceivermodalComponent implements OnInit, OnDestroy {
  receiverForm: FormGroup;
  collisId: string;
  agencyCode: string;
  ticketNumber: string;
  isSubmitting = false;

  phoneSearchResults: CustomerOption[] = [];
  nameSearchResults: CustomerOption[] = [];
  isSearchingPhone = false;
  isSearchingName = false;

  private phoneSearch$ = new Subject<string>();
  private nameSearch$ = new Subject<string>();
  private subs = new Subscription();

  constructor(
    private formBuilder: FormBuilder,
    private dialogRef: MatDialogRef<ChangereceivermodalComponent>,
    @Inject(MAT_DIALOG_DATA) public data: any,
    private collisService: CollisService,
    private toastrService: ToastrService
  ) {
    this.collisId = data?.collisId || '';
    this.agencyCode = data?.agencyCode || '';
    this.ticketNumber = data?.ticket || '';
    this.receiverForm = this.formBuilder.group({
      receivernumber: [data?.currentReceiverPhone || '', Validators.required],
      receivername: [data?.currentReceiverName || '', Validators.required]
    });
  }

  ngOnInit(): void {
    // Setup debounced phone search from DB
    this.subs.add(
      this.phoneSearch$
        .pipe(
          debounceTime(300),
          distinctUntilChanged(),
          switchMap((val) => {
            const query = (val || '').trim();
            if (query.length < 3) {
              this.isSearchingPhone = false;
              this.phoneSearchResults = [];
              return of([]);
            }
            this.isSearchingPhone = true;
            return this.collisService.searchcustomerbynumber(query).pipe(
              catchError((err) => {
                console.error('Error searching customer by number:', err);
                return of([]);
              })
            );
          })
        )
        .subscribe((res: any) => {
          this.isSearchingPhone = false;
          const results: CustomerOption[] = [];
          if (Array.isArray(res)) {
            for (const phoneItem of res) {
              const phoneStr = phoneItem?.contact || '';
              if (phoneItem?.customerDTO && Array.isArray(phoneItem.customerDTO) && phoneItem.customerDTO.length > 0) {
                for (const cust of phoneItem.customerDTO) {
                  results.push({
                    phone: phoneStr,
                    name: cust?.name || ''
                  });
                }
              } else if (phoneStr) {
                results.push({
                  phone: phoneStr,
                  name: ''
                });
              }
            }
          }
          this.phoneSearchResults = results;
        })
    );

    // Setup debounced name search from DB
    this.subs.add(
      this.nameSearch$
        .pipe(
          debounceTime(300),
          distinctUntilChanged(),
          switchMap((val) => {
            const query = (val || '').trim();
            if (query.length < 2) {
              this.isSearchingName = false;
              this.nameSearchResults = [];
              return of([]);
            }
            this.isSearchingName = true;
            return this.collisService.searchcustomerbyname(query).pipe(
              catchError((err) => {
                console.error('Error searching customer by name:', err);
                return of([]);
              })
            );
          })
        )
        .subscribe((res: any) => {
          this.isSearchingName = false;
          const results: CustomerOption[] = [];
          if (Array.isArray(res)) {
            for (const custItem of res) {
              const custName = custItem?.name || '';
              if (custItem?.phoneDTO && Array.isArray(custItem.phoneDTO) && custItem.phoneDTO.length > 0) {
                for (const phoneItem of custItem.phoneDTO) {
                  results.push({
                    name: custName,
                    phone: phoneItem?.contact || ''
                  });
                }
              } else if (custName) {
                results.push({
                  name: custName,
                  phone: ''
                });
              }
            }
          }
          this.nameSearchResults = results;
        })
    );
  }

  onPhoneKeyup(event: any): void {
    const val = event.target.value;
    this.phoneSearch$.next(val);
  }

  onNameKeyup(event: any): void {
    const val = event.target.value;
    this.nameSearch$.next(val);
  }

  onSelectPhoneOption(opt: CustomerOption): void {
    if (opt.phone) {
      this.receiverForm.get('receivernumber')?.setValue(opt.phone, { emitEvent: false });
    }
    if (opt.name) {
      this.receiverForm.get('receivername')?.setValue(opt.name, { emitEvent: false });
    }
    this.phoneSearchResults = [];
  }

  onSelectNameOption(opt: CustomerOption): void {
    if (opt.name) {
      this.receiverForm.get('receivername')?.setValue(opt.name, { emitEvent: false });
    }
    if (opt.phone) {
      this.receiverForm.get('receivernumber')?.setValue(opt.phone, { emitEvent: false });
    }
    this.nameSearchResults = [];
  }

  ngOnDestroy(): void {
    this.subs.unsubscribe();
  }

  onSubmit(): void {
    if (this.receiverForm.invalid || this.isSubmitting) {
      return;
    }

    this.isSubmitting = true;
    const body = this.receiverForm.value;
    this.collisService.changeReceiver(this.collisId, body, this.agencyCode).subscribe(
      (res: any) => {
        this.isSubmitting = false;
        this.toastrService.success('Destinataire modifié avec succès', 'Succès');
        this.dialogRef.close({
          success: true,
          receivername: body.receivername,
          receivernumber: body.receivernumber,
          updatedCollis: res
        });
      },
      (err: any) => {
        this.isSubmitting = false;
        console.error('Error changing receiver:', err);
        const msg = err.error?.message || (typeof err.error === 'string' ? err.error : null) || 'Impossible de modifier le destinataire.';
        this.toastrService.error(msg, 'Erreur');
      }
    );
  }

  onCancel(): void {
    this.dialogRef.close(false);
  }
}
